import React, { useState, useEffect } from "react";
import { FileText, Sparkles, CheckCircle2, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { HIGHLIGHT_CONFIG } from "./highlights";

export const BuildingHighlightState = ({
  color,
}: {
  color: (typeof HIGHLIGHT_CONFIG)[number]["color"];
}) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    { icon: FileText, label: "Searching course documents", delay: 0 },
    { icon: Sparkles, label: "Analyzing course content", delay: 3000 },
    { icon: Zap, label: "Building study materials", delay: 6000 },
    { icon: CheckCircle2, label: "Finalizing materials", delay: 10000 },
  ];

  useEffect(() => {
    // Progress bar animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return prev + 1;
      });
    }, 100);

    // Step animation
    const stepTimers = steps.map((step, index) =>
      setTimeout(() => setCurrentStep(index), step.delay)
    );

    return () => {
      clearInterval(progressInterval);
      stepTimers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div className="bg-theme-0 px-6 py-6">
      <div className="space-y-4">
        {/* Active step indicator */}
        <div className="flex items-center justify-center gap-3 relative">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isComplete = index < currentStep;

            return (
              <div key={index} className="flex items-center gap-3">
                <div
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-lg border-2 transition-all duration-300",
                    isActive && `${color.bg} ${color.border} scale-110`,
                    isComplete && `${color.bgDark} ${color.border}`,
                    !isActive && !isComplete && "bg-theme-0 border-theme"
                  )}
                >
                  <Icon
                    className={cn(
                      "h-4 w-4 transition-colors duration-300",
                      isActive || isComplete ? "text-theme-0" : "text-theme-2",
                      isActive && "animate-pulse"
                    )}
                  />
                </div>
                {index < steps.length - 1 && (
                  <div className="h-0.5 w-8 bg-theme-1">
                    <div
                      className={cn(
                        `h-full ${color.bg} transition-all duration-500`,
                        isComplete ? "w-full" : "w-0"
                      )}
                    />
                  </div>
                )}
              </div>
            );
          })}
          {/* Particle effects */}
          {/* <div className="h-40 overflow-hidden absolute -top-6 left-0 right-0 -bottom-0">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className={cn(
                  "absolute h-1.5 w-1.5 rounded-full",
                  color.bgDark,
                  "animate-float"
                )}
                style={{
                  left: `${i * 12}%`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${2 + i * 0.3}s`,
                }}
              />
            ))}
          </div> */}
        </div>

        {/* Current step label */}
        <div className="text-sm font-semibold text-theme-0">
          {steps[currentStep].label}...
        </div>

        {/* Progress bar */}
        <div className="relative h-2 rounded-full bg-theme-2 overflow-hidden">
          <div
            className={`absolute inset-y-0 left-0 transition-all ${color.bgDark} duration-100 ease-linear rounded-full`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Building blocks animation */}
        <div className="space-y-2 mt-6">
          {[0, 1, 2].map((blockIndex) => (
            <div
              key={blockIndex}
              className={cn(
                "rounded-lg border-2 border-theme bg-theme-0 p-3 transition-all duration-500",
                currentStep > blockIndex
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-4"
              )}
              style={{ transitionDelay: `${blockIndex * 200}ms` }}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-2">
                  {/* Title bar building */}
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        "h-3 rounded bg-theme-2 transition-all duration-700",
                        currentStep > blockIndex ? "w-2/5" : "w-0"
                      )}
                      style={{ transitionDelay: `${blockIndex * 200 + 100}ms` }}
                    />
                    <div
                      className={cn(
                        "h-2 rounded bg-theme-1 transition-all duration-700",
                        currentStep > blockIndex ? "w-1/5" : "w-0"
                      )}
                      style={{ transitionDelay: `${blockIndex * 200 + 300}ms` }}
                    />
                  </div>

                  {/* Content lines building */}
                  <div className="space-y-1.5">
                    {[0, 1].map((lineIndex) => (
                      <div
                        key={lineIndex}
                        className={cn(
                          "h-2 rounded bg-theme-1 transition-all duration-700",
                          currentStep > blockIndex ? "w-full" : "w-0"
                        )}
                        style={{
                          transitionDelay: `${
                            blockIndex * 200 + 500 + lineIndex * 200
                          }ms`,
                          width:
                            currentStep > blockIndex
                              ? lineIndex === 1
                                ? "80%"
                                : "100%"
                              : "0%",
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Check icon appears when complete */}
                {currentStep > blockIndex + 1 && (
                  <CheckCircle2 className="h-4 w-4 text-green-500 animate-in zoom-in duration-300" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(48px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-12px);
            opacity: 0;
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
